import React, { useMemo, useState } from "react";
import { Button, InputNumber, Select, Space, Typography } from "antd";
import { DoubleRightOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import { fetchInventoryChannels } from "../../../../api/fulfillment/product";
import { orderBy } from "lodash-es";
import { LabeledValue } from "antd/lib/select";
import { titleCase } from "../../../../components/SelectSalesChannel";
import { Wrapper } from "./styles";
import { TransferStockPayload } from "../../../../api/fulfillment/inventoryChannels";
import { Facility } from "../../../../api/common/facility";
import { useFacilityIndex } from "hooks/api/common/useFacilityIndex";
import { fetchInventory } from "api/inventory";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlagQuery";

type ChannelStockReallocationProps = {
  orgId: string;
  productId: string;
  onCancel: () => void;
  onSubmit: (payload: TransferStockPayload) => void;
};

const order = (option: LabeledValue[]) => orderBy(option, ["value"], ["asc"]);

const INITIAL_STATE = {
  facility: undefined,
  from_channel_name: undefined,
  to_channel_name: undefined,
  quantity: 0,
};

export const ChannelStockReallocation: React.FC<ChannelStockReallocationProps> = ({
  orgId,
  productId,
  onCancel,
  onSubmit,
}) => {
  const [payload, setPayload] = useState<Partial<TransferStockPayload>>(INITIAL_STATE);

  const useNewWarehouseInventory = useFeatureFlagQuery(
    "use-new-warehouse-inventory-values",
    { organization_id: orgId },
    { enabled: !!orgId }
  );

  const { data: facilities = [], ...facilitiesQuery } = useFacilityIndex({
    select<LabeledValue>(data: Facility[]) {
      const facilities = data.filter((d) => d.name !== "unknown" && d.enabled);
      return order(
        facilities.map((f) => ({ value: f.name, label: `${f.address.city} / ${f.name.toUpperCase()}` }))
      ) as unknown as LabeledValue[];
    },
  });

  const { data: channels = [], ...channelsQuery } = useQuery(["channels", orgId], () => fetchInventoryChannels(orgId), {
    enabled: !!orgId,
    select({ data }) {
      return order(data.map((c) => ({ value: c.name, label: titleCase(c.name) })));
    },
  });

  const { data: inventory = {}, ...inventoryQuery } = useQuery(
    ["inventory", payload.facility, channels.length, productId],
    () =>
      Promise.all(
        channels.map((channel) =>
          fetchInventory({
            version: useNewWarehouseInventory.data?.state ? 2 : 1,
            product_id: productId,
            organization_id: orgId,
            channels: [channel.value] as string[],
            facilities: payload.facility ? [payload.facility] : undefined,
            collate: false,
          })
        )
      ),
    {
      enabled: !!payload.facility && !!channels.length,
      select(data) {
        return data.reduce(
          (acc, cur) => ({
            ...acc,
            ...(cur.channels && {
              [cur.channels[0]]: useNewWarehouseInventory.data?.state
                ? cur.available_to_pick
                : cur.available_for_work_orders,
            }),
          }),
          {} as Record<string, number>
        );
      },
    }
  );

  const channelOptions = useMemo(() => {
    return channels.map((channel) => ({ ...channel, label: channel.label + ` (${inventory[channel.value] || 0})` }));
  }, [channels, inventory]);

  return (
    <Wrapper>
      <Space direction="vertical">
        <Typography.Text>Facility:</Typography.Text>
        <Select
          options={facilities as unknown as LabeledValue[]}
          value={payload.facility}
          onChange={(facility: TransferStockPayload["facility"]) => setPayload((state) => ({ ...state, facility }))}
          loading={facilitiesQuery.isLoading}
        />
      </Space>
      <Wrapper row align="center">
        <Space direction="vertical">
          <Typography.Text>From Channel:</Typography.Text>
          <Select
            loading={channelsQuery.isLoading || inventoryQuery.isLoading}
            options={channelOptions.filter((c) => c.value !== payload.to_channel_name)}
            value={payload.from_channel_name}
            onChange={(from_channel_name: TransferStockPayload["from_channel_name"]) =>
              setPayload((state) => ({ ...state, from_channel_name }))
            }
            allowClear
          />
        </Space>
        <DoubleRightOutlined />
        <Space direction="vertical">
          <Typography.Text>To Channel:</Typography.Text>
          <Select
            loading={channelsQuery.isLoading || inventoryQuery.isLoading}
            options={channelOptions.filter((c) => c.value !== payload.from_channel_name)}
            value={payload.to_channel_name}
            onChange={(to_channel_name: TransferStockPayload["to_channel_name"]) =>
              setPayload((state) => ({ ...state, to_channel_name }))
            }
            allowClear
          />
        </Space>
      </Wrapper>
      <Space direction="vertical">
        <Typography.Text>Quantity To Reallocate:</Typography.Text>
        <InputNumber
          min={0}
          max={inventory[payload.from_channel_name || ""]}
          value={payload.quantity || 0}
          onChange={(quantity) => setPayload((state) => ({ ...state, quantity: quantity || 0 }))}
          onKeyDown={(e) => {
            const key = e.key.toLowerCase();
            if (["backspace", "delete"].includes(key)) return;
            if (!/^\d+$/.test(key)) e.preventDefault();
          }}
        />
      </Space>

      <Space className="actions">
        <Button
          onClick={() => {
            setPayload(INITIAL_STATE);
            onCancel();
          }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          disabled={Object.values(payload).some((value) => !value)}
          onClick={() =>
            onSubmit({ ...payload, organization_id: orgId, product_id: productId } as TransferStockPayload)
          }
        >
          Submit
        </Button>
      </Space>
    </Wrapper>
  );
};
